/**
 * ForwardListPage — `/purchase/contract/forward-pu` (Forward P tab).
 *
 * 1:1 Access port of `frmPurchaseMainSubFormLocalP_ver2` + subform
 * `frmPurchaseQryLocalPver2` (RecordSource view `v_local_p_ver2`):
 *   - Header: Edit + Add Deposit (mirrors Access btnEdit / btnAddDeposit)
 *   - Datasheet: 39 cols in Access ColumnOrder + ColumnWidth (twips)
 *   - Edit blocker parity: NPE fixed / cancelled / forced-close
 *   - Add Deposit blocker parity: NPE fixed
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '@/theme/responsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '@/hooks/usePagePermission'
import { useLazyGetLocalPQuery } from '@/store/api/localPApi'
import { useListLocalPVer2Query } from '@/store/api/localPVer2Api'
import type { LocalPDetail, LocalPVer2Item } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import DepositFormDialog from './DepositFormDialog'
import LocalPFormDialog from './LocalPFormDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type FilterKind = 'search'
type ColumnKind = 'text' | 'date' | 'number' | 'percent'

type ColumnDef = {
  key: Extract<keyof LocalPVer2Item, string>
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  /** Access ColumnWidth (twips). -2 = best fit, undefined = unspecified. */
  width?: number
  /** Freeze cột khi scroll ngang — className `app-sticky-col`. */
  sticky?: boolean
}

const ALL_ROWS_PAGE_SIZE = 1_000_000

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

// Column display order = TabIndex (Access datasheet view rule) of
// `frmPurchaseQryLocalPver2`. Caption + ColumnWidth (twips) per subform.
// ControlSource = column in `PurchaseQryLocalPver2` query → `v_local_p_ver2`.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no', label: 'Contract No', width: -2, sticky: true },
  { key: 'npe_ref', label: 'NPE Ref', width: -2 },
  { key: 'contract_date', label: 'Contract Date', kind: 'date', width: -2 },
  { key: 'supplier_code', label: 'Supplier Code', filter: 'checkboxes', width: -2 },
  { key: 'name', label: 'Supplier Name', filter: 'checkboxes', width: -2 },
  { key: 'quantity_kg', label: 'Kg', kind: 'number', align: 'right', width: -2 },
  { key: 'quality_code', label: 'Quality Code', filter: 'checkboxes', width: -2 },
  { key: 'certification', label: 'Certification', filter: 'checkboxes', width: -2 },
  { key: 'contract_type', label: 'Contract Type', filter: 'checkboxes', width: -2 },
  { key: 'outright_price', label: 'Outright Price', kind: 'number', align: 'right', width: -2 },
  { key: 'ptbf_diffs', label: 'PTBF Diffs', kind: 'number', align: 'right', width: -2 },
  { key: 'price_unit', label: 'Price Unit', width: -2 },
  { key: 'futures_month', label: 'Futures Month', width: -2 },
  { key: 'fx', label: 'FX', kind: 'number', align: 'right', width: -2 },
  { key: 'received', label: 'Received', kind: 'number', align: 'right', width: -2 },
  { key: 'to_be_received', label: 'To be Received', kind: 'number', align: 'right', width: -2 },
  { key: 'deposit_value_per', label: 'Deposit Rate (%)', kind: 'percent', align: 'right', width: 1836 },
  { key: 'deposit_limit_per', label: 'Deposit Limit (%)', kind: 'percent', align: 'right', width: -2 },
  { key: 'deposit_time_limit', label: 'Time Limit', width: -2 },
  { key: 'deposit_bonus_per', label: 'Top up (%)', kind: 'percent', align: 'right', width: 1308 },
  { key: 'deposit_interest_per', label: 'Deposit Interest (% per month)', kind: 'percent', align: 'right', width: -2 },
  { key: 'deposit_repayment_date', label: 'Deposit Repayment Date', kind: 'date', width: -2 },
  { key: 'count_first_deposit', label: 'First Deposit - Count', kind: 'number', align: 'right', width: -2 },
  { key: 'count_top_up', label: 'Top Up - Count', kind: 'number', align: 'right', width: -2 },
  { key: 'count_repayment', label: 'Repayment - Count', kind: 'number', align: 'right', width: -2 },
  { key: 'first_deposit_value', label: 'First Deposit Value', kind: 'number', align: 'right', width: -2 },
  { key: 'first_deposit_actual', label: 'First Deposit Actual', kind: 'number', align: 'right', width: -2 },
  { key: 'reconcile', label: 'Reconcile', kind: 'number', align: 'right', width: -2 },
  { key: 'deposit_price', label: 'Deposit Price', kind: 'number', align: 'right', width: -2 },
  { key: 'market_price_w_basis', label: 'Market Price with Premium', kind: 'number', align: 'right', width: 2760 },
  { key: 'top_up_alert', label: 'Top Up Alert', filter: 'checkboxes', width: -2 },
  { key: 'top_up_value', label: 'Top Up Value', kind: 'number', align: 'right', width: -2 },
  { key: 'top_up_actual', label: 'Top Up Actual', kind: 'number', align: 'right', width: -2 },
  { key: 'top_up_date', label: 'Top Up Date', width: -2 },
  { key: 'repayment_actual', label: 'Repayment Actual', kind: 'number', align: 'right', width: -2 },
  { key: 'total_deposit', label: 'Total Deposit', kind: 'number', align: 'right', width: -2 },
  { key: 'total_interest', label: 'Total Interest', kind: 'number', align: 'right', width: -2 },
  { key: 'total_repayment', label: 'Total Repayment', kind: 'number', align: 'right', width: -2 },
  { key: 'tolerance', label: 'Tolerance', kind: 'number', align: 'right', width: -2 },
]

/** Access twips → CSS px (1440 twips = 1 inch ≈ 96 px → 1 twip ≈ 0.0667 px). */
const accessTwipsToPx = (twips?: number): number | undefined => {
  if (twips == null || twips < 0) return undefined
  return Math.round((twips * 96) / 1440)
}

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return String(value)
  return `${`${date.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[date.getMonth()]}-${`${date.getFullYear()}`}`
}

const formatAccessNumber = (value: number): string =>
  Number.isInteger(value)
    ? value.toLocaleString('en-US')
    : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const cellValue = (row: LocalPVer2Item, column: ColumnDef): string => {
  const value = row[column.key]
  if (value == null) return ''
  if (column.kind === 'date') return formatAccessDate(value)
  if (column.kind === 'percent' && typeof value === 'number') return `${formatAccessNumber(value * 100)}%`
  if (column.kind === 'number' && typeof value === 'number') return formatAccessNumber(value)
  if (typeof value === 'number') return formatAccessNumber(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const warnSelectedRecordRequired = 'Please choose a record!'
const warnNpeFixed = 'This P Contract is fixed from NPE Contract!'
const warnCancel = 'This P Contract is cancel! No edit!'
const warnClosed = 'This P Contract is closed! No edit!'

interface ForwardListPageProps {
  /** Header filter from PurchaseContractPage. YYYY-MM-DD or ''. */
  deliveryLatestFrom?: string
  deliveryLatestTo?: string
  /** 'all' | 'yes' | 'no' — mirror LocalPListPage. */
  isForcedClose?: 'all' | 'yes' | 'no'
}

export function ForwardListPage({
  deliveryLatestFrom,
  deliveryLatestTo,
  isForcedClose,
}: ForwardListPageProps = {}) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const [fetchLocalPDetail, { isFetching: isFetchingEditDetail }] = useLazyGetLocalPQuery()

  const {
    selectedId: selectedContractNo,
    toggleSelect: toggleSelectContract,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.forward')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof LocalPVer2Item> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, event: MouseEvent<HTMLElement>) =>
    filterPopover.open(kind, event)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof LocalPVer2Item
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const [depositContractNo, setDepositContractNo] = useState<string | null>(null)
  const [editingContract, setEditingContract] = useState<LocalPDetail | null>(null)

  const forcedCloseParam =
    isForcedClose === 'yes' ? true : isForcedClose === 'no' ? false : undefined
  const { data, isLoading, isFetching, error } = useListLocalPVer2Query({
    page: 1,
    page_size: ALL_ROWS_PAGE_SIZE,
    delivery_latest_from: deliveryLatestFrom || undefined,
    delivery_latest_to: deliveryLatestTo || undefined,
    is_forced_close: forcedCloseParam,
  }, { refetchOnMountOrArgChange: true })

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'forward-list',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.forward',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () =>
      Math.max(
        3200,
        visibleColumns.reduce((total, col) => total + (accessTwipsToPx(col.width) ?? 140), 0),
      ),
    [visibleColumns],
  )

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, visibleColumns), [filteredRows, sortState, visibleColumns])
  const rows = useMemo(
    () => (rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
    [page, rowsPerPage, sortedRows],
  )

  const selectedRow = useMemo(
    () => baseRows.find((row) => row.contract_no === selectedContractNo) ?? null,
    [baseRows, selectedContractNo],
  )

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const toggleSort = (key: keyof LocalPVer2Item) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  const ensureSelectedRow = (): LocalPVer2Item | null => {
    if (!selectedRow) {
      snack.warning(warnSelectedRecordRequired)
      return null
    }
    return selectedRow
  }

  const handleEdit = async () => {
    const row = ensureSelectedRow()
    if (!row) return
    if ((row.npe_ref ?? '').trim()) {
      snack.warning(warnNpeFixed)
      return
    }

    try {
      const response = await fetchLocalPDetail(row.contract_no).unwrap()
      const detail = response.data
      if (detail.cancel_adjust !== null && detail.cancel_adjust !== undefined) {
        snack.warning(warnCancel)
        return
      }
      if (detail.is_forced_close) {
        snack.warning(warnClosed)
        return
      }
      setEditingContract(detail)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to load P Contract'))
    }
  }

  const handleAddDeposit = () => {
    const row = ensureSelectedRow()
    if (!row) return
    if ((row.npe_ref ?? '').trim()) {
      snack.warning(warnNpeFixed)
      return
    }
    setDepositContractNo(row.contract_no)
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!canEdit || isFetchingEditDetail}
              onClick={() => void handleEdit()}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title={isFetchingEditDetail ? 'Loading...' : 'Edit'}
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{isFetchingEditDetail ? 'Loading...' : 'Edit'}</Box>
            </Button>
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleAddDeposit}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="Add Deposit"
            >
              {/* Pattern 10: "Deposit" luôn show, prefix "Add " ẩn xs+sm */}
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Add{' '}</Box>
              Deposit
            </Button>
          </Stack>
        }
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(event) => openFilterPopover('search', event)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(event: MouseEvent<HTMLElement>) => openMenu(event.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) ? <CircularProgress size={15} /> : null}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPage(0)
              }}
              placeholder="Contract, supplier, NPE ref..."
              label="Search Forward P"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 320,
            overflow: 'hidden',
            '& .topup-alert-row': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.16) },
            },
            /* Freeze cell trong CF row — layer alpha red overlay trên solid white.
               `.app-sticky-col` CSS chung set bg `#ffffff` (opaque chống bleed-through
               horizontal scroll) → đè lên row's transparent red. Layered backgrounds
               (color + linear-gradient overlay) tái tạo cùng visual với row bg.
               !important cần thiết để thắng CSS `tr:hover/Mui-selected td.app-sticky-col`. */
            '& .topup-alert-row .app-sticky-col': {
              backgroundColor: (theme) => `${theme.palette.background.paper} !important`,
              backgroundImage: (theme) =>
                `linear-gradient(${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.08)}) !important`,
            },
            '& .topup-alert-row:hover .app-sticky-col': {
              backgroundImage: (theme) =>
                `linear-gradient(${alpha(theme.palette.error.main, 0.16)}, ${alpha(theme.palette.error.main, 0.16)}) !important`,
            },
            '& .topup-alert-cell': {
              bgcolor: (theme) => theme.palette.error.light,
              color: (theme) => theme.palette.error.contrastText,
              fontWeight: 700,
            },
          }}
        >
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Forward P"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Forward P found"
              description="Try adjusting the table filters above."
            />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((column) => {
                  const px = accessTwipsToPx(column.width)
                  const w = getWidth(String(column.key))
                  return (
                    <TableCell
                      key={String(column.key)}
                      align={column.align}
                      className={`app-table-header-cell${column.sticky ? ' app-sticky-col' : ''}`}
                      onClick={() => toggleSort(column.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        ...(!w && px ? { width: px, minWidth: px } : {}),
                      }}
                    >
                      {column.label}
                      {getTableSortSuffix(sortState, column.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(column.key))}
                        onClick={(position) => setColPopover({ key: column.key, position })}
                        ariaLabel={`Filter ${column.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(String(column.key), e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: LocalPVer2Item) =>
              visibleColumns.map((column) => {
                const value = cellValue(row, column)
                const isAlertCell = column.key === 'top_up_alert' && row.top_up_alert && row.top_up_alert !== ''
                const w = getWidth(String(column.key))
                return (
                  <TableCell
                    key={String(column.key)}
                    align={column.align}
                    className={`${isAlertCell ? 'topup-alert-cell' : ''}${column.sticky ? ' app-sticky-col' : ''}`.trim()}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Box component="span" title={value || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                      {value || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const index = Number((props as any)['data-item-index'])
                const row = Number.isFinite(index) ? rows[index] : null
                const className = row?.top_up_alert ? 'topup-alert-row' : ''
                return (
                  <TableRow
                    {...props}
                    className={className}
                    hover
                    selected={row?.contract_no != null && row.contract_no === selectedContractNo}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (row?.contract_no) toggleSelectContract(row.contract_no)
                    }}
                  />
                )
              },
            }}
          />
          )}
        </Box>
      </Box>

      <LocalPFormDialog
        open={!!editingContract}
        mode="edit"
        initialValue={editingContract}
        onClose={() => setEditingContract(null)}
      />

      <DepositFormDialog
        open={depositContractNo !== null}
        mode="create"
        fixedContractNo={depositContractNo ?? undefined}
        ver2Row={depositContractNo ? selectedRow : null}
        showDetailList
        onClose={() => setDepositContractNo(null)}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}

export default ForwardListPage
